import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  api,
  type ExecutiveMemoDetailData,
  type ExecutiveMemoPriority,
} from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  EXECUTIVE_MEMO_KIND_OPTIONS,
  EXECUTIVE_MEMO_PRIORITY_OPTIONS,
  buildCommaSeparatedValue,
  buildExecutiveMemoUpdatePayload,
  createExecutiveMemoDetailDraft,
  formatExecutiveMemoDateTime,
  formatExecutiveMemoKindLabel,
  formatExecutiveMemoPriorityLabel,
  getBackendMessage,
  parseCommaSeparatedValues,
} from "../lib/executiveMemos";

type MemoDraft = ReturnType<typeof createExecutiveMemoDetailDraft>;


function MemoDetailField({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-2 border-b border-[#F0E4DA] py-2.5 last:border-0">
      <span className="min-w-[6rem] shrink-0 text-xs text-[#9C867D]">{label}</span>
      <span className="text-right text-sm font-bold text-[#4A3E39]">{value}</span>
    </div>
  );
}

export default function ExecutiveMemoDetailPage() {
  const navigate = useNavigate();
  const { memoId } = useParams<{ memoId: string }>();

  const [memo, setMemo] = useState<ExecutiveMemoDetailData | null>(null);
  const [draft, setDraft] = useState<MemoDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sourceImageUrl, setSourceImageUrl] = useState<string | null>(null);
  const [sourceImageExpiresAt, setSourceImageExpiresAt] = useState<string | null>(null);
  const [sourceImageLoading, setSourceImageLoading] = useState(false);
  const [sourceImageError, setSourceImageError] = useState<string | null>(null);
  const [sourceImageUnavailable, setSourceImageUnavailable] = useState(false);

  const loadMemo = useCallback(async () => {
    if (!memoId) return;

    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.getExecutiveMemo(memoId);
      setMemo(response);
      setDraft(createExecutiveMemoDetailDraft(response));
    } catch (error) {
      logError(error);
      setErrorMessage(error instanceof AppError && error.code === "NOT_FOUND" ? "メモが見つかりませんでした。" : toUserMessage(error));
    } finally {
      setLoading(false);
    }
  }, [memoId]);

  useEffect(() => {
    void loadMemo();
  }, [loadMemo]);

  const handleSave = async () => {
    if (!memo || !draft || !memoId) return;

    setSaving(true);
    setErrorMessage(null);
    setSaveMessage(null);

    try {
      const payload = buildExecutiveMemoUpdatePayload(draft, memo.version);
      const response = await api.updateExecutiveMemo(memoId, payload);
      setMemo(response);
      setDraft(createExecutiveMemoDetailDraft(response));
      setEditing(false);
      setSaveMessage("保存しました。");
    } catch (error) {
      logError(error);
      if (error instanceof AppError && error.code === "CONFLICT") {
        setConflictOpen(true);
      } else {
        setErrorMessage(getBackendMessage(error, ["title", "body", "kind", "themes", "tags", "priority"]) ?? toUserMessage(error));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (memo) setDraft(createExecutiveMemoDetailDraft(memo));
    setEditing(false);
    setErrorMessage(null);
  };

  const handleReloadAfterConflict = async () => {
    setConflictOpen(false);
    await loadMemo();
    setSaveMessage("最新版を読み込みました。編集中の内容は破棄されました。");
  };

  const handleShowSourceImage = async () => {
    if (!memoId || sourceImageUnavailable) return;

    setSourceImageLoading(true);
    setSourceImageError(null);
    try {
      const response = await api.getExecutiveMemoSourceImage(memoId);
      setSourceImageUrl(response.url);
      setSourceImageExpiresAt(response.expiresAt);
    } catch (error) {
      logError(error);
      if (error instanceof AppError && error.meta.status === 410) {
        setSourceImageUnavailable(true);
        setSourceImageError("原本画像の保管期限が過ぎて削除されています。");
      } else {
        setSourceImageError(toUserMessage(error));
      }
    } finally {
      setSourceImageLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!memoId) return;

    setDeleting(true);
    setErrorMessage(null);
    try {
      await api.deleteExecutiveMemo(memoId);
      navigate("/executive-memos", { replace: true });
    } catch (error) {
      logError(error);
      setErrorMessage(toUserMessage(error));
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-10 text-center text-sm text-[#8A746A]">
        読み込み中...
      </main>
    );
  }

  if (!memo || !draft) {
    return (
      <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-10 text-[#4A3E39]">
        <div className="mx-auto max-w-2xl rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
          {errorMessage ?? "メモを表示できませんでした。"}
          <div className="mt-4">
            <Link to="/executive-memos" className="font-bold text-[#7C5A4E] underline">
              ←戻る
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/executive-memos" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">社長メモ</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <div className="mb-6">
          <h1 className="text-2xl font-black tracking-tight text-[#7C5A4E]">{memo.title || "無題のメモ"}</h1>
          <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#9C867D]">
            {memo.capturedAt && (
              <div className="flex gap-1">
                <dt>撮影:</dt>
                <dd>{formatExecutiveMemoDateTime(memo.capturedAt)}</dd>
              </div>
            )}
            <div className="flex gap-1">
              <dt>作成:</dt>
              <dd>{formatExecutiveMemoDateTime(memo.createdAt)}</dd>
            </div>
            <div className="flex gap-1">
              <dt>更新:</dt>
              <dd>{formatExecutiveMemoDateTime(memo.updatedAt)}</dd>
            </div>
          </dl>
        </div>

        {errorMessage ? (
          <div role="alert" className="mb-5 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
            {errorMessage}
          </div>
        ) : null}
        {saveMessage ? (
          <div className="mb-5 rounded-2xl border border-[#D8E5D2] bg-[#F4FAF1] px-4 py-3 text-sm text-[#3F5C3B]">
            {saveMessage}
          </div>
        ) : null}
        {sourceImageError ? (
          <div role="alert" className="mb-5 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
            {sourceImageError}
          </div>
        ) : null}

        {!editing ? (
          <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
            {memo.body && (
              <div className="mb-5 whitespace-pre-wrap text-sm leading-7 text-[#4A3E39]">
                {memo.body}
              </div>
            )}

            <div className="mb-5">
              {memo.kind && <MemoDetailField label="種別" value={formatExecutiveMemoKindLabel(memo.kind)} />}
              <MemoDetailField label="優先度" value={formatExecutiveMemoPriorityLabel(memo.priority)} />
              {memo.themes.length > 0 && <MemoDetailField label="テーマ" value={buildCommaSeparatedValue(memo.themes)} />}
              {memo.tags.length > 0 && <MemoDetailField label="タグ" value={buildCommaSeparatedValue(memo.tags)} />}
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleShowSourceImage}
                disabled={sourceImageLoading || sourceImageUnavailable || !memo.sourceImage.available}
                className="inline-flex items-center gap-1 rounded-full border border-[#A67C52] px-3 py-1.5 text-xs font-bold text-[#A67C52] hover:bg-[#F5EDE8] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sourceImageLoading ? "取得中..." : "原本画像を表示"}
              </button>
            </div>

            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => { setEditing(true); setErrorMessage(null); setSaveMessage(null); }}
                className="w-full rounded-full border border-[#A67C52] py-2.5 text-sm font-bold text-[#A67C52] hover:bg-[#F5EDE8]"
              >
                内容を修正する
              </button>
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(true)}
                className="w-full rounded-full border border-[#C9867D] py-2.5 text-sm font-bold text-[#A1493E] hover:bg-[#FFF0EE]"
              >
                このメモを削除
              </button>
            </div>
          </section>
        ) : (
          <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
            <h2 className="mb-4 text-lg font-black text-[#5D4D46]">編集</h2>
            <div className="grid gap-5">
              <label className="block">
                <span className="mb-1 block text-sm font-bold text-[#5D4D46]">タイトル</span>
                <input
                  value={draft.title}
                  onChange={(event) => setDraft((prev) => prev ? { ...prev, title: event.target.value } : prev)}
                  className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-bold text-[#5D4D46]">本文</span>
                <textarea
                  value={draft.body}
                  rows={10}
                  onChange={(event) => setDraft((prev) => prev ? { ...prev, body: event.target.value } : prev)}
                  className="w-full resize-y rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-sm font-bold text-[#5D4D46]">種別</span>
                  <select
                    value={draft.kind}
                    onChange={(event) => setDraft((prev) => prev ? { ...prev, kind: event.target.value } : prev)}
                    className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none"
                  >
                    {EXECUTIVE_MEMO_KIND_OPTIONS.map((kind) => (
                      <option key={kind} value={kind}>
                        {kind}
                      </option>
                    ))}
                    {draft.kind && !(EXECUTIVE_MEMO_KIND_OPTIONS as readonly string[]).includes(draft.kind) && (
                      <option value={draft.kind}>{formatExecutiveMemoKindLabel(draft.kind)}</option>
                    )}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-bold text-[#5D4D46]">優先度</span>
                  <select
                    value={draft.priority}
                    onChange={(event) => setDraft((prev) => prev ? { ...prev, priority: event.target.value as ExecutiveMemoPriority } : prev)}
                    className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none"
                  >
                    {EXECUTIVE_MEMO_PRIORITY_OPTIONS.map((priority) => (
                      <option key={priority} value={priority}>
                        {formatExecutiveMemoPriorityLabel(priority)}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-bold text-[#5D4D46]">テーマ</span>
                  <input
                    value={buildCommaSeparatedValue(draft.themes)}
                    onChange={(event) => setDraft((prev) => prev ? { ...prev, themes: parseCommaSeparatedValues(event.target.value) } : prev)}
                    className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block text-sm font-bold text-[#5D4D46]">タグ</span>
                  <input
                    value={buildCommaSeparatedValue(draft.tags)}
                    onChange={(event) => setDraft((prev) => prev ? { ...prev, tags: parseCommaSeparatedValues(event.target.value) } : prev)}
                    className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                  />
                </label>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full rounded-full bg-[#7C5A4E] px-5 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(124,90,78,0.16)] transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "保存中..." : "保存する"}
                </button>
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="w-full rounded-full border border-[#D8C0AF] py-2.5 text-sm font-bold text-[#7C5A4E] hover:bg-[#FFF7F2]"
                >
                  キャンセル
                </button>
              </div>
            </div>
          </section>
        )}
      </div>

      {sourceImageUrl ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6">
          <div className="max-h-full w-full max-w-4xl overflow-auto rounded-[2rem] bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-bold text-[#7C5A4E]">
                期限: {formatExecutiveMemoDateTime(sourceImageExpiresAt)}
              </p>
              <button
                type="button"
                onClick={() => setSourceImageUrl(null)}
                aria-label="閉じる"
                className="rounded-full border border-[#D8C0AF] bg-white px-3 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52]"
              >
                閉じる
              </button>
            </div>
            <img src={sourceImageUrl} alt="原本画像" className="mx-auto max-h-[75vh] max-w-full rounded-2xl object-contain" />
          </div>
        </div>
      ) : null}

      {conflictOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-[2rem] bg-white p-5 shadow-lg">
            <h2 className="text-lg font-black text-[#5D4D46]">最新版があります</h2>
            <p className="mt-3 text-sm leading-6 text-[#8A746A]">
              他のデバイスで更新された可能性があります。最新版を読み直しますか？
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConflictOpen(false)}
                className="rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52]"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleReloadAfterConflict}
                className="rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
              >
                読み直す
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteConfirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-[2rem] bg-white p-5 shadow-lg">
            <h2 className="text-lg font-black text-[#5D4D46]">削除しますか？</h2>
            <p className="mt-3 text-sm leading-6 text-[#8A746A]">
              「{memo.title || "無題のメモ"}」を削除すると元に戻せません。
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                className="rounded-full border border-[#D8C0AF] bg-white px-4 py-2 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52]"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-full bg-[#A1493E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#8C3A33] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deleting ? "削除中..." : "削除する"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}
